import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import Loader from '../../components/Common/Loader';
import Modal from '../../components/Common/Modal';
import Input from '../../components/Common/Input';
import Button from '../../components/Common/Button';
import CardMenu from '../../components/Common/CardMenu';
import DateBadge from '../../components/Common/DateBadge';
import VariantNumbersPane from '../../components/Admin/VariantNumbersPane';
import { confirmDialog } from '../../components/Common/confirm';
import {
  getCourses, getVariants, createVariant, updateVariant, deleteVariant, duplicateVariant,
  getSections, reorderLessons, updateCourse, publishAllInCourse, getDeletedInCourse, restoreLesson,
} from '../../services/adminService';
import toast from 'react-hot-toast';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const IcClip = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
    <rect x="9" y="3" width="6" height="4" rx="2"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="12" y2="16"/>
  </svg>
);
const IcCloud = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>);
const IcEdit = () => (<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
const IcTrash = () => (<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>);
const IcDup = () => (<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="8" width="12" height="12" rx="2"/><path d="M4 16V6a2 2 0 0 1 2-2h10"/></svg>);
const IcCopy = () => (<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>);
const IcPaste = () => (<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>);
const IcRestore = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><path d="M12 7v5l3 2"/></svg>);
const IcCloudUp = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/><polyline points="9 13 12 10 15 13"/><line x1="12" y1="10" x2="12" y2="17"/></svg>);
const IcArrowRight = () => (<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>);
const IcPlus = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);

const ShellCover = ({ id }) => (
  <svg aria-hidden="true" width="100%" height="100%" style={{ position: 'absolute', inset: 0, color: 'var(--cc-pat)', pointerEvents: 'none' }}>
    <defs>
      <pattern id={`shv-${id}`} width="130" height="130" patternUnits="userSpaceOnUse" patternTransform="rotate(-6)">
        <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.055">
          <g transform="translate(8,9)"><rect x="0" y="4" width="26" height="19" rx="4" /><path d="M0 10 h26" /></g>
          <g transform="translate(70,7)"><path d="M0 0 l14 8 l-14 8 z" /></g>
          <g transform="translate(102,10)"><circle cx="8" cy="8" r="8" /><path d="M13.7 13.7 l6 6" /></g>
          <g transform="translate(40,38)"><path d="M11 0 l11 6 v13 l-11 6 l-11 -6 v-13 z" /><path d="M0 6 l11 6 l11 -6 M11 12 v13" /></g>
          <g transform="translate(92,42)"><path d="M0 8 a14 14 0 0 1 20 0" /><path d="M4 13 a9 9 0 0 1 12 0" /><circle cx="10" cy="19" r="1.6" fill="currentColor" stroke="none" /></g>
          <g transform="translate(6,74)"><path d="M0 4 h9 l3 3 h14 a2.5 2.5 0 0 1 2.5 2.5 v11 a2.5 2.5 0 0 1 -2.5 2.5 h-26 a2.5 2.5 0 0 1 -2.5 -2.5 v-13.5 a2.5 2.5 0 0 1 2.5 -2.5 z" /></g>
          <g transform="translate(104,72)"><path d="M0 0 l6 5 l-6 5" /><path d="M10 12 h9" /></g>
          <g transform="translate(62,92)"><path d="M0 0 l0 22 l5 -6 l3.2 7.5 l3.4 -1.5 l-3.2 -7.5 l7.6 -0.8 z" /></g>
        </g>
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill={`url(#shv-${id})`} />
  </svg>
);

const plural = (n, forms) => {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return forms[2];
  if (b > 1 && b < 5) return forms[1];
  if (b === 1) return forms[0];
  return forms[2];
};

// Дата варианта — в подписи, как у ученика в «Вариантах»: «12.12»
const dayMonth = (d) => {
  const x = d ? new Date(d) : null;
  if (!x || isNaN(x)) return '';
  return `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}`;
};
const varMeta = (v) => [
  `${v.taskCount} ${plural(v.taskCount, ['номер', 'номера', 'номеров'])}`,
  v.examType,
  dayMonth(v.date),
].filter(Boolean).join(' · ');
// Слева — иконка раздела «Варианты» (как у ученика), у черновика — облако
const VarIcon = ({ draft }) => (draft ? <DateBadge draft /> : <span className="test-date"><IcClip /></span>);

// ── Карточка варианта (sortable) ──
const VariantCard = ({ v, onOpen, menuItems, dragDisabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: 'var-' + v.id, disabled: dragDisabled,
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0 : 1 };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="avar-card" onClick={() => onOpen(v.id)}>
      <VarIcon draft={!v.isPublished} />
      <div className="avar-main">
        <div className="avar-title">{v.title}</div>
        <div className="avar-meta">{varMeta(v)}</div>
      </div>
      {!v.isPublished && <span className="pg-chip amber" style={{ flexShrink: 0 }}>Черновик</span>}
      <div className="avar-actions" onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}>
        <CardMenu items={menuItems(v)} />
        <button className="pg-btn" onClick={() => onOpen(v.id)}><IcEdit /> Редактировать</button>
      </div>
    </div>
  );
};

const AdminVariantsPage = ({ courseId: fixedCourseId, search = '', icon, course, onCourseChanged, focusVariantId, onFocusDone }) => {
  // Менеджер и куратор варианты только смотрят
  const { can } = useAuth();
  const ro = !can('courses.edit');
  const embedded = fixedCourseId != null;
  const [courses, setCourses] = useState([]);
  const [activeCourse, setActiveCourse] = useState(embedded ? fixedCourseId : null);
  const [variants, setVariants] = useState([]);
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [openVariantId, setOpenVariantId] = useState(null);
  const [clipboard, setClipboard] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [courseName, setCourseName] = useState(course?.name || '');
  const [publishing, setPublishing] = useState(false);
  const [restoreMode, setRestoreMode] = useState(false);
  const [deletedVariants, setDeletedVariants] = useState([]);
  const [activeDrag, setActiveDrag] = useState(null);
  const suppressClick = useRef(false);

  useEffect(() => { setCourseName(course?.name || ''); }, [course?.name]);

  useEffect(() => {
    getCourses().then(res => {
      const list = res.data || [];
      setCourses(list);
      setActiveCourse(embedded ? fixedCourseId : (list[0]?.id || null));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [embedded, fixedCourseId]);

  const reload = () => getVariants(activeCourse).then(res => setVariants(res.data || [])).catch(() => {});

  useEffect(() => {
    if (!activeCourse) { setVariants([]); setWebinars([]); return; }
    reload();
    getSections(activeCourse).then(res => {
      const list = [];
      (res.data || []).forEach(s => (s.Lessons || []).forEach(l => { if (!l.isHomeworkOnly && !l.isVariant) list.push({ id: l.id, title: l.title, section: s.name }); }));
      setWebinars(list);
    }).catch(() => setWebinars([]));
  }, [activeCourse]); // eslint-disable-line

  const hasDrafts = variants.some(v => !v.isPublished);

  const handleCreate = async () => {
    const title = newTitle.trim();
    if (!title) return toast.error('Введите название варианта');
    setSaving(true);
    try {
      const res = await createVariant({ courseId: activeCourse, title });
      setCreateOpen(false); setNewTitle('');
      await reload();
      setOpenVariantId(res.data.id);
    } catch (e) { toast.error(e.response?.data?.message || 'Ошибка'); }
    finally { setSaving(false); }
  };
  const handleDelete = async (v) => {
    if (!(await confirmDialog({ title: 'Удалить вариант?', message: 'Можно восстановить в режиме восстановления.', confirmText: 'Удалить' }))) return;
    try { await deleteVariant(v.id); toast.success('Вариант удалён'); reload(); }
    catch { toast.error('Ошибка при удалении'); }
  };
  const togglePub = async (v) => { try { await updateVariant(v.id, { isPublished: !v.isPublished }); toast.success(v.isPublished ? 'Снято с публикации' : 'Опубликовано'); reload(); } catch { toast.error('Ошибка'); } };
  const dup = async (v) => { try { await duplicateVariant(v.id); toast.success('Вариант дублирован'); reload(); } catch { toast.error('Ошибка'); } };
  const paste = async () => { if (!clipboard) return; try { await duplicateVariant(clipboard.id); toast.success('Вставлено'); reload(); } catch { toast.error('Ошибка вставки'); } };
  const openRename = (v) => { setRenameTarget(v.id); setRenameVal(v.title); };
  const submitRename = async () => { const t = renameVal.trim(); if (!t || !renameTarget) { setRenameTarget(null); return; } try { await updateVariant(renameTarget, { title: t }); setRenameTarget(null); reload(); } catch { toast.error('Ошибка'); } };

  const renameCourse = async () => {
    const name = courseName.trim();
    if (!name || name === course?.name) { setCourseName(course?.name || ''); return; }
    try { await updateCourse(activeCourse, { name }); onCourseChanged && onCourseChanged(); toast.success('Курс переименован'); }
    catch { toast.error('Ошибка'); setCourseName(course?.name || ''); }
  };
  const publishAll = async () => {
    if (!hasDrafts || publishing) return;
    setPublishing(true);
    try { await publishAllInCourse(activeCourse); await reload(); toast.success('Все варианты опубликованы'); }
    catch { toast.error('Ошибка'); } finally { setPublishing(false); }
  };
  const openRestore = async () => {
    try { const r = await getDeletedInCourse(activeCourse); setDeletedVariants(r.data?.variants || []); } catch { setDeletedVariants([]); }
    setRestoreMode(true);
  };
  const doRestore = async (id) => { try { await restoreLesson(id); toast.success('Вариант восстановлен'); const r = await getDeletedInCourse(activeCourse); setDeletedVariants(r.data?.variants || []); reload(); } catch { toast.error('Ошибка'); } };

  const menuItems = (v) => ro ? [] : [
    { label: 'Переименовать', icon: <IcEdit />, onClick: () => openRename(v) },
    { label: v.isPublished ? 'Снять с публикации' : 'Опубликовать', icon: <IcCloud />, onClick: () => togglePub(v) },
    { label: 'Дублировать', icon: <IcDup />, onClick: () => dup(v) },
    { label: 'Копировать', icon: <IcCopy />, onClick: () => { setClipboard({ id: v.id, title: v.title }); toast('Вариант скопирован', { icon: '📋' }); } },
    { label: 'Вставить', icon: <IcPaste />, disabled: !clipboard, onClick: () => paste() },
    { label: 'Удалить', icon: <IcTrash />, danger: true, onClick: () => handleDelete(v) },
  ];

  // ── Drag-reorder вариантов ──
  const variantsRef = useRef(variants); variantsRef.current = variants;
  const dragDisabled = !!search.trim() || ro;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const openVariant = (id) => { if (suppressClick.current) return; setOpenVariantId(id); };

  // Переход с «Главной» по конкретному варианту — сразу открываем его редактор
  useEffect(() => {
    if (!focusVariantId || variants.length === 0) return;
    if (!variants.some(v => v.id === focusVariantId)) return;
    setOpenVariantId(focusVariantId);
    onFocusDone && onFocusDone();
  }, [focusVariantId, variants]); // eslint-disable-line
  const handleDragStart = (e) => { setActiveDrag(variantsRef.current.find(v => 'var-' + v.id === e.active.id) || null); suppressClick.current = false; };
  const handleDragEnd = (e) => {
    setActiveDrag(null);
    suppressClick.current = true; setTimeout(() => { suppressClick.current = false; }, 80);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setVariants(prev => {
      const oldI = prev.findIndex(v => 'var-' + v.id === active.id);
      const newI = prev.findIndex(v => 'var-' + v.id === over.id);
      if (oldI < 0 || newI < 0) return prev;
      const next = arrayMove(prev, oldI, newI);
      reorderLessons(next.map(v => v.id)).catch(() => { toast.error('Не удалось сохранить порядок'); reload(); });
      return next;
    });
  };

  if (loading) return <Loader />;

  const activeCourseObj = courses.find(c => c.id === activeCourse);
  if (openVariantId) {
    return (
      <VariantNumbersPane variantId={openVariantId} icon={icon} webinars={webinars}
        onBack={() => { setOpenVariantId(null); reload(); }} onMetaChanged={reload} />
    );
  }

  const q = search.trim().toLowerCase();
  const shown = q ? variants.filter(v => (v.title || '').toLowerCase().includes(q)) : variants;

  const grid = (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setActiveDrag(null)}>
      <SortableContext items={shown.map(v => 'var-' + v.id)} strategy={verticalListSortingStrategy}>
        <div className="avar-list">
          {shown.map(v => <VariantCard key={v.id} v={v} onOpen={openVariant} menuItems={menuItems} dragDisabled={dragDisabled} />)}
          {!q && !ro && activeCourse && (
            <button className="avar-add" onClick={() => { setNewTitle(''); setCreateOpen(true); }}>
              <div className="avar-add-ic"><IcPlus /></div>
              <div style={{ textAlign: 'left' }}>
                <div className="avar-add-t" style={{ fontSize: 14, fontWeight: 700, color: 'var(--primary-dark)' }}>Новый вариант</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-3)', fontWeight: 500, marginTop: 2 }}>пробник с автопроверкой</div>
              </div>
            </button>
          )}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={{ duration: 300, easing: 'cubic-bezier(0.34, 1.25, 0.64, 1)' }}>
        {activeDrag && (
          <div className="avar-card cc-overlay">
            <VarIcon draft={!activeDrag.isPublished} />
            <div className="avar-main">
              <div className="avar-title">{activeDrag.title}</div>
              <div className="avar-meta">{varMeta(activeDrag)}</div>
            </div>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );

  const restorePanel = (
    <div className="cc-restore">
      <div className="cc-restore-head">
        <div className="t"><IcRestore /> Режим восстановления</div>
      </div>
      {deletedVariants.length === 0
        ? <div className="cc-empty-leaf" style={{ padding: '22px 8px' }}>Удалённых вариантов нет</div>
        : (
          <div className="avar-list">
            {deletedVariants.map(v => (
              <div key={v.id} className="avar-card" style={{ cursor: 'default' }}>
                <VarIcon />
                <div className="avar-main">
                  <div className="avar-title">{v.title}</div>
                  <div className="avar-meta">Удалённый вариант</div>
                </div>
                <button className="pg-btn" style={{ flexShrink: 0, background: 'rgba(16,185,129,0.12)', color: '#0f9d6f' }} onClick={() => doRestore(v.id)}>
                  <IcRestore /> Восстановить
                </button>
              </div>
            ))}
          </div>
        )}
    </div>
  );

  const shellHead = (
    <div className="cc-shell-head" style={{ marginBottom: 14 }}>
      <div className="cc-topcard-main">
        <input className="cc-topcard-name" value={courseName} onChange={e => setCourseName(e.target.value)}
          onBlur={renameCourse} onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }} placeholder="Название курса" />
        <div className="cc-topcard-sub">Пробные варианты курса</div>
      </div>
      {/* Порядок как в «Вебинарах» и «Заданиях»: сначала публикация, потом восстановление */}
      {!restoreMode && !ro && (
        <button className="cc-top-ic pub" onClick={publishAll} disabled={!hasDrafts || publishing}
          title={hasDrafts ? 'Опубликовать все варианты' : 'Всё опубликовано'}><IcCloudUp /></button>
      )}
      {restoreMode ? (
        <button className="cc-top-ic back" onClick={() => setRestoreMode(false)} title="Вернуться к вариантам"><IcArrowRight /></button>
      ) : !ro && (
        <button className="cc-top-ic" onClick={openRestore} title="Восстановление удалённых"><IcRestore /></button>
      )}
    </div>
  );

  return (
    <div>
      {embedded ? (
        <div className="cc-shell">
          <ShellCover id={activeCourse} />
          {shellHead}
          <div className="avar-swipe-win">
            <div className="cc-swipe" style={{ transform: restoreMode ? 'translateX(-50%)' : 'none' }}>
              <div className="cc-swipe-pane restore">{restorePanel}</div>
              <div className="cc-swipe-pane list">{grid}</div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 20 }}>
            <h1 className="s-page-title">Варианты</h1>
            <p className="s-page-sub">Пробные экзамены курса · редактирование номеров</p>
          </div>
          {courses.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              {courses.map(c => {
                const active = activeCourse === c.id;
                return (
                  <button key={c.id} onClick={() => setActiveCourse(c.id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 30,
                      border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`, cursor: 'pointer', fontFamily: 'inherit',
                      fontSize: 13, fontWeight: active ? 700 : 500,
                      background: active ? 'var(--primary)' : 'var(--card)', color: active ? '#fff' : 'var(--text-2)',
                      boxShadow: active ? '0 6px 16px -6px rgba(124,77,255,0.6)' : 'none', transition: 'all .15s' }}>
                    {c.name}{c.examType ? ` · ${c.examType}` : ''}
                  </button>
                );
              })}
            </div>
          )}
          {q && shown.length === 0 ? <div className="pg-empty">Ничего не найдено</div> : grid}
        </>
      )}

      <Modal isOpen={createOpen} onClose={() => setCreateOpen(false)} title="Новый вариант" size="small">
        <div className="form-group">
          <label className="form-label">Название варианта</label>
          <input value={newTitle} onChange={e => setNewTitle(e.target.value)} autoFocus
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
            placeholder={activeCourseObj?.examType === 'ОГЭ' ? 'Пробный ОГЭ №1' : 'Пробный ЕГЭ №1'}
            style={{ width: '100%', padding: '11px 13px', borderRadius: 11, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text-1)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>
        <div className="form-actions" style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="action-btn secondary" onClick={() => setCreateOpen(false)}>Отмена</button>
          <button className="action-btn purple" onClick={handleCreate} disabled={saving}>{saving ? 'Создание...' : 'Создать и редактировать'}</button>
        </div>
      </Modal>

      <Modal isOpen={!!renameTarget} onClose={() => setRenameTarget(null)} title="Переименовать вариант">
        <Input label="Название" value={renameVal} onChange={(e) => setRenameVal(e.target.value)} required />
        <div className="form-actions">
          <Button variant="secondary" onClick={() => setRenameTarget(null)}>Отмена</Button>
          <Button variant="purple" onClick={submitRename}>Сохранить</Button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminVariantsPage;
